import { SimpleTable } from "@asup/simple-table";
import { getTemplateHeaders } from "components/common/tlfItemTable/functions/getTemplateHeaders";
import { mapTlfItemTableRow } from "components/common/tlfItemTable/functions/mapTlfItemTableRow";
import { eHierarchyLevel } from "functions/api/enums";
import { TlfItemIndexReadDto } from "functions/api/tlfItem/TlfItemIndexReadDto";
import { getReportTlfSelection } from "../../hierarchybrowser/getReportTlfSelection";
import { useHBC } from "../../hierarchybrowser/HierarchyBrowserContextProvider";
import { SET_SELECTIONS } from "../../hierarchybrowser/interface";
import "./TlfItemTable.css";

/**
 * Sources displayed as independently selectable Master and named-variant rows.
 */
export interface ReportVariantSelectionsProps {
  disabled?: boolean;
  items: TlfItemIndexReadDto[];
}

/**
 * Offers independent outputs using the existing table's search, filtering and sorting.
 *
 * @param props - Persisted sources and whether report copying is currently blocked
 * @param props.disabled - Prevents selecting outputs while SaveAs has outstanding edits
 * @param props.items - Source summaries, including the available named variants
 * @returns The selectable source/output rows
 */
export const ReportVariantSelections = ({
  disabled = false,
  items,
}: ReportVariantSelectionsProps) => {
  const { state, dispatch } = useHBC();
  if (!state || !dispatch) return null;
  const rows = items.flatMap((item) =>
    [undefined, ...(item.variantTemplates ?? []).map((variant) => variant.name)].map((name) => ({
      ...mapTlfItemTableRow({
        ...item,
        domain:
          item.variantTemplates?.find((variant) => variant.name === name)?.domain ?? item.domain,
      }),
      selectionKey: JSON.stringify([item.id, name]),
      variantName: name ?? ((item.variantTemplates ?? []).length > 0 ? "Master" : ""),
      selectedVariantName: name,
    })),
  );
  const selected = rows
    .filter((row) => {
      const selection = state.selections.find((entry) => entry.id === row.id);
      return row.selectedVariantName === undefined
        ? selection?.includeMaster === true
        : (selection?.variantNames ?? []).includes(row.selectedVariantName);
    })
    .map((row) => row.selectionKey);

  return (
    <SimpleTable
      currentSelection={selected}
      data={rows}
      fields={getTemplateHeaders(
        false,
        false,
        items[0]?.primaryHierarchyLevel ?? eHierarchyLevel.Corporate,
        false,
        true,
      )}
      filterLabel="Hide retired/decommissioned"
      id="report-variant-selections"
      initialFilterSelected
      keyField="selectionKey"
      selectable={!disabled}
      selectedBackgroundColor="rgba(0, 0, 0, 0.2)"
      setCurrentSelection={(keys) => {
        const selectedRows = rows.filter((row) => keys.includes(row.selectionKey));
        dispatch({
          operation: SET_SELECTIONS,
          selections: [
            ...state.selections.filter((entry) => !items.some((item) => item.id === entry.id)),
            ...items.flatMap((item) => {
              const outputs = selectedRows.filter((row) => row.id === item.id);
              return getReportTlfSelection(
                item,
                outputs.some((row) => row.selectedVariantName === undefined),
                outputs.flatMap((row) =>
                  row.selectedVariantName === undefined ? [] : [row.selectedVariantName],
                ),
              );
            }),
          ],
        });
      }}
      showFilter
      showSearch
      tableClassName="tlf-item-table"
    />
  );
};

ReportVariantSelections.displayName = "ReportVariantSelections";
